/**
 * Generic Wizard Step Navigation
 * Displays list of steps with completion status
 * Extracted from KYCStepNavigation and generified
 */

import React from 'react';
import { useWizardContext } from './WizardContext';
import {
  Check,
  ChevronRight,
  Eye
} from 'lucide-react';
import { StepStatus } from './types';

const WizardStepNavigation: React.FC = () => {
  const {
    config,
    currentStep,
    completedSteps,
    reviewedSteps,
    reviewMode,
    goToStep,
    canNavigateToStep
  } = useWizardContext();

  /**
   * Get step status for styling
   */
  const getStepStatus = (stepId: number): StepStatus => {
    if (stepId === currentStep) return 'current';

    if (reviewMode) {
      if (reviewedSteps && reviewedSteps.has(stepId)) return 'reviewed';
    } else {
      if (completedSteps.has(stepId)) return 'completed';
    }

    return 'upcoming';
  };

  /**
   * Calculate completion percentage
   */
  const completionPercentage = React.useMemo(() => {
    const totalSteps = config.steps.length;
    const completedCount = reviewMode
      ? (reviewedSteps?.size || 0)
      : completedSteps.size;

    return Math.round((completedCount / totalSteps) * 100);
  }, [config.steps.length, completedSteps.size, reviewedSteps, reviewMode]);

  return (
    <div className="h-full bg-kielis-glacier">
      <div className="p-6">
        <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
          Form Sections
        </h2>

        <nav className="space-y-1" role="navigation" aria-label="Form sections">
          {config.steps.map((step) => {
            const stepNumber = step.id;

            // In review mode, optionally hide first step (document upload)
            if (reviewMode && config.features.reviewMode && stepNumber === 1) {
              return null;
            }

            const StepIcon = step.icon;
            const status = getStepStatus(stepNumber);
            const isActive = status === 'current';
            const isDone = status === 'completed' || status === 'reviewed';
            const isClickable = canNavigateToStep(stepNumber);

            return (
              <button
                key={step.id}
                onClick={() => goToStep(stepNumber)}
                disabled={!isClickable}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-lg
                  transition-all duration-200 group
                  ${isActive
                    ? `${config.theme.activeStepBg || 'bg-kielis-navy'} ${config.theme.activeStepText || 'text-white'} shadow-md`
                    : isDone
                      ? status === 'reviewed'
                        ? 'bg-white text-gray-700 hover:bg-gray-50 border border-blue-200'
                        : `${config.theme.completedStepBg || 'bg-white'} text-gray-700 hover:bg-gray-50 border ${config.theme.completedStepBorder || 'border-green-200'}`
                      : isClickable
                        ? `${config.theme.upcomingStepBg || 'bg-white'} ${config.theme.upcomingStepText || 'text-gray-600'} hover:bg-gray-50 border border-gray-200`
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                  }
                `}
                aria-current={isActive ? 'step' : undefined}
                aria-label={`${step.name} - Step ${stepNumber} of ${config.steps.length}`}
              >
                {/* Step number / completion indicator */}
                <div className="flex-shrink-0">
                  {isDone ? (
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      status === 'reviewed' ? 'bg-blue-500' : 'bg-green-500'
                    }`}>
                      {status === 'reviewed' ? (
                        <Eye className="w-4 h-4 text-white" />
                      ) : (
                        <Check className="w-4 h-4 text-white" />
                      )}
                    </div>
                  ) : reviewMode ? (
                    // In review mode, show icon instead of number
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center
                      ${isActive ? 'bg-kielis-gold' : 'bg-gray-200'}
                    `}>
                      {StepIcon ? (
                        <StepIcon className={`w-4 h-4 ${isActive ? 'text-kielis-navy' : 'text-gray-600'}`} />
                      ) : (
                        <span className={`text-sm font-medium ${isActive ? 'text-kielis-navy' : 'text-gray-600'}`}>
                          {stepNumber}
                        </span>
                      )}
                    </div>
                  ) : (
                    // In normal mode, show step number
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm
                      ${isActive ? 'bg-kielis-gold text-kielis-navy' : 'bg-gray-200 text-gray-600'}
                    `}>
                      {stepNumber}
                    </div>
                  )}
                </div>

                {/* Icon and label */}
                <div className="flex-1 flex items-center gap-3">
                  {StepIcon && !reviewMode && (
                    <StepIcon className={`w-5 h-5 ${isActive ? 'text-kielis-gold' : ''}`} />
                  )}
                  <span className="font-medium text-sm text-left">
                    {step.shortName || step.name}
                  </span>
                </div>

                {/* Status indicators */}
                <div className="flex items-center gap-2">
                  {isActive && (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </div>
              </button>
            );
          })}
        </nav>

        {/* Progress summary card */}
        <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              {reviewMode ? 'Review Progress' : 'Completion'}
            </span>
            <span className="text-sm font-semibold text-kielis-navy">
              {completionPercentage}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`${config.theme.progressBarColor || 'bg-kielis-gold'} h-2 rounded-full transition-all duration-500`}
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {reviewMode
              ? `${reviewedSteps?.size || 0} of ${config.steps.length} sections reviewed`
              : `${completedSteps.size} of ${config.steps.length} sections completed`
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default WizardStepNavigation;
